import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { withLatestFrom } from 'rxjs/operators';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { DashboardClaim, Request, Response } from '@lsq-frontend/remote-services/enterprise/common';
import { ApiService } from '@lsq-frontend/angular-shared/api-service';
import { BackdropService } from '../../../services/backdrop/backdrop.service';
import { ClaimService } from '../../../services/claim/claim.service';
import { FloatingActionButtonService } from '../../../services/floating-action-button/floating-action-button.service';
import { ResponsiveService } from '../../../services/responsive/responsive.service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';

@LifecycleAware()
@Component({
    selector: 'lsq-feedback-form',
    templateUrl: './lsq-feedback-form.component.html',
    styleUrls: ['./lsq-feedback-form.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class LsqFeedbackFormComponent implements OnInit, OnDestroy {
    @Input() public sideButtonText: string;
    @Input() public formTitle: string;
    @Input() public messageLabel?: string;
    @Input() public responseMessage?: string;
    @Input() public addService?: ApiService<any, any>;
    @Input() public displayRatingStars = false;

    public displayForm = false;
    public formGroup: FormGroup;
    public selectedRating: number;
    public hoveredRating: number;
    public icons = Icons;
    public formSubmitted = false;
    public userInContext = false;
    public color = Color;
    private _totalRatingCount = 5;
    public starArray = new Array(this._totalRatingCount).fill(1);
    public isMobile = false;

    constructor(
        private _userService: UserService,
        private _router: Router,
        private _responsiveService: ResponsiveService,
        private _floatingActionButtonService: FloatingActionButtonService,
        private _claimService: ClaimService,
        public backdropService: BackdropService
    ) {}

    public ngOnInit(): void {
        this.formGroup = new FormGroup({
            message: new FormControl('')
        });

        this._responsiveService.smBelow.pipe(forLifeOf(this)).subscribe((isMobile) => {
            if (isMobile && !this.isMobile && this.displayForm) {
                this.openBackdrop();
            }

            if (this.isMobile && !isMobile && this.displayForm) {
                this.backdropService.closeBackDrop();
            }
            this.isMobile = isMobile;
        });

        this._userService.logout$.pipe(forLifeOf(this)).subscribe((loggedOut) => {
            if (loggedOut) {
                this.userInContext = false;
                this._floatingActionButtonService.removeFab('feedback');
            }
        });

        this._userService.user$
            .pipe(
                forLifeOf(this),
                withLatestFrom(this._claimService.isOn(DashboardClaim.FinancingEnrollment))
            )
            .subscribe(([user, isFinancingUser]) => {
                if (user && !user.isImpersonating && !!user.currentCompany && !isFinancingUser) {
                    this.userInContext = true;
                    this._floatingActionButtonService.addAction({
                        identifier: 'feedback',
                        icon: this.icons.Star,
                        action: this.openForm.bind(this),
                        global: true
                    });
                } else {
                    this._floatingActionButtonService.removeFab('feedback');
                    this.userInContext = false;
                }
            });
    }

    public ngOnDestroy(): void {}

    private openBackdrop(): void {
        this.backdropService.openBackdrop({ onClick: this.close.bind(this) });
    }

    public selectRating(rating: number): void {
        this.selectedRating = rating;
    }

    public handleMouseEnter(rating: number): void {
        this.hoveredRating = rating;
    }

    public handleMouseLeave(): void {
        delete this.hoveredRating;
    }

    public openForm(event: any): void {
        if (!this.displayForm) {
            this.displayForm = true;
            if (this.isMobile) {
                this.openBackdrop();
            }
        } else {
            this.close();
        }
        event.stopImmediatePropagation();
    }

    public handleSubmit(e: Event): void {
        e.preventDefault();
        this.formSubmitted = true;
        const request = new this.addService.requestType();
        request.message = this.formGroup.controls.message.value || '';
        request.companyKey = this._userService.getCompanyKey();
        request.route = this._router.url;
        if (this.selectedRating) {
            request.rating = this.selectedRating;
        }
        this.addService.execute(request);
        this.selectedRating = null;
        this.formGroup.reset();
    }

    public close(): void {
        if (this.isMobile) {
            this.backdropService.closeBackDrop();
        }
        this.displayForm = false;
        this.formSubmitted = false;
        this.formGroup.reset();
        delete this.selectedRating;
        delete this.hoveredRating;
    }

    public shouldDisableSubmit(): boolean {
        const messageValue = this.formGroup.controls.message.value;
        return !this.selectedRating && (!messageValue || !messageValue.length);
    }

    public getStarColor(starIndex: number): string {
        if (this.hoveredRating) {
            if (starIndex <= this.hoveredRating) {
                return this.color.Yellow;
            }
        } else if (this.selectedRating && starIndex <= this.selectedRating) {
            return this.color.Yellow;
        }

        return this.color.GrayLightest;
    }
}
